/**
 * Status-Anzeige + manueller Sync-Button im Header.
 *
 * Status-Logik (in Reihenfolge der Priorität):
 *  - N Konflikte               → roter "Konflikte: N"-Pill, Klick öffnet Modal
 *  - während Sync              → "Synchronisiere…" (gesperrt)
 *  - online + 0 pending        → "Online"
 *  - online + N pending        → "Sync · N" (Klick triggert Sync)
 *  - offline + 0 pending       → "Offline"
 *  - offline + N pending       → "Offline · N ausstehend"
 */

import { useEffect, useState } from 'react';
import { useSync } from '../data/useSync';
import { SYNC_FAILED_EVENT } from '../db/sync';
import { useConflictCount } from '../data/conflicts';
import { ConflictsModal } from './ConflictsModal';

export function SyncIndicator() {
  const { online, pendingCount, isSyncing, lastSync, triggerSync } = useSync();
  const conflictCount = useConflictCount();
  const [showConflicts, setShowConflicts] = useState(false);
  // Gesamtprüfung 11.09.2026: vom Server abgelehnte Offline-Änderungen
  // sichtbar machen (vorher nur console.warn — die Änderung war einfach weg).
  const [failures, setFailures] = useState<string[]>([]);
  useEffect(() => {
    function onFailed(ev: Event) {
      const msg = (ev as CustomEvent<{ message?: string }>).detail?.message ?? 'Unbekannter Fehler';
      setFailures((prev) => [...prev.slice(-9), msg]);
    }
    window.addEventListener(SYNC_FAILED_EVENT, onFailed);
    return () => window.removeEventListener(SYNC_FAILED_EVENT, onFailed);
  }, []);

  if (failures.length > 0 && conflictCount === 0) {
    return (
      <button
        onClick={() => {
          alert(
            `Diese Offline-Änderung${failures.length === 1 ? ' wurde' : 'en wurden'} vom Server nicht übernommen:\n\n• ${failures.join('\n• ')}`,
          );
          setFailures([]);
        }}
        className="text-xs px-2 py-1 rounded-full !min-h-0 bg-red-500/20 text-red-300 hover:bg-red-500/30"
        aria-live="polite"
        title="Klick: Details anzeigen"
      >
        ⚠ {failures.length} nicht übernommen
      </button>
    );
  }

  // 1. Konflikte → priorisiert
  if (conflictCount > 0) {
    return (
      <>
        <button
          onClick={() => setShowConflicts(true)}
          className="text-xs px-2 py-1 rounded-full !min-h-0 bg-red-500/20 text-red-300 hover:bg-red-500/30"
          aria-live="polite"
          title="Klick: Konflikte anzeigen"
        >
          ⚠ {conflictCount} Konflikt{conflictCount > 1 ? 'e' : ''}
        </button>
        {showConflicts && <ConflictsModal onClose={() => setShowConflicts(false)} />}
      </>
    );
  }

  const label = isSyncing
    ? 'Synchronisiere…'
    : !online
      ? pendingCount > 0
        ? `Offline · ${pendingCount} ausstehend`
        : 'Offline'
      : pendingCount > 0
        ? `Sync · ${pendingCount}`
        : 'Online';

  const color = isSyncing
    ? 'bg-blue-500/20 text-blue-300'
    : !online
      ? 'bg-amber-500/20 text-amber-300'
      : pendingCount > 0
        ? 'bg-amber-500/20 text-amber-300'
        : 'bg-emerald-500/20 text-emerald-300';

  return (
    <button
      onClick={() => void triggerSync()}
      disabled={isSyncing || !online}
      className={`text-xs px-2 py-1 rounded-full !min-h-0 ${color} disabled:opacity-60`}
      aria-live="polite"
      title={
        lastSync
          ? `Letzter Sync: ${new Date(lastSync).toLocaleString('de-DE')}`
          : 'Noch nicht synchronisiert'
      }
    >
      {label}
    </button>
  );
}
